import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';

import { auth, db } from '../firebase';

export const BUNDLE_ARCHITECTURE_COLLECTIONS = {
  mixedProductInputs: 'mixedProductInputs',
  mixedProductComponents: 'mixedProductComponents',
  bundles: 'bundles',
  bundleComponents: 'bundleComponents',
  bundleExtraCosts: 'bundleExtraCosts',
};

const normalizeString = (value) =>
  typeof value === 'string' ? value.trim() : '';

const normalizeNumber = (value, fallback = null) => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : fallback;
};

const normalizeBoolean = (value, fallback = false) =>
  typeof value === 'boolean' ? value : fallback;

const normalizeStatus = (value) =>
  ['draft', 'active'].includes(value) ? value : 'draft';

const normalizeSnapshotData = (value, fallback = null) =>
  value && typeof value === 'object' ? value : fallback;

const normalizeDimensionNumber = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? Number(numericValue.toFixed(6)) : null;
};

const CALCULATION_MODE_VALUES = ['fixed', 'area_wh', 'area_wl', 'volume_whl'];
const DIMENSION_FIELDS = ['width', 'height', 'length'];

const normalizeCalculationMode = (value) =>
  CALCULATION_MODE_VALUES.includes(value) ? value : 'fixed';

const normalizeDimensionFields = (value = []) => {
  const fields = Array.isArray(value) ? value : [];

  return Array.from(
    new Set(
      fields
        .map((field) => normalizeString(field).toLowerCase())
        .filter((field) => DIMENSION_FIELDS.includes(field))
    )
  );
};

const inferDimensionFields = (value = {}) => {
  const storedFields = normalizeDimensionFields(
    value?.fields || value?.requiredInputs
  );

  if (storedFields.length > 0) {
    return storedFields;
  }

  return DIMENSION_FIELDS.filter(
    (field) => normalizeDimensionNumber(value?.[field]) !== null
  );
};

const normalizeConfigurationDimensions = (value = {}) => {
  const calculationMode = normalizeCalculationMode(value?.calculationMode);
  const fields = inferDimensionFields(value);
  const width = normalizeDimensionNumber(value?.width);
  const height = normalizeDimensionNumber(value?.height);
  const length = normalizeDimensionNumber(value?.length);
  const area =
    calculationMode === 'area_wh' && width !== null && height !== null
      ? normalizeDimensionNumber(width * height)
      : calculationMode === 'area_wl' && width !== null && length !== null
        ? normalizeDimensionNumber(width * length)
        : value?.area !== undefined && value?.volume === undefined
          ? normalizeDimensionNumber(value.area)
          : null;
  const volume =
    calculationMode === 'volume_whl' &&
    width !== null &&
    height !== null &&
    length !== null
      ? normalizeDimensionNumber(width * height * length)
      : value?.volume !== undefined
        ? normalizeDimensionNumber(value.volume)
        : null;

  return {
    width: fields.includes('width') || width !== null ? width : null,
    height: fields.includes('height') || height !== null ? height : null,
    length: fields.includes('length') || length !== null ? length : null,
    area,
    volume,
    calculationMode,
    fields,
    enabled: fields.length > 0 || width !== null || height !== null || length !== null,
  };
};

const getConfigurationDimensionKey = (dimensions = {}) => {
  const normalizedDimensions = normalizeConfigurationDimensions(dimensions);

  if (!normalizedDimensions.enabled) {
    return '';
  }

  return (normalizedDimensions.fields || [])
    .map((field) => normalizedDimensions[field] ?? '')
    .join('x');
};

const mapDocumentSnapshot = (docSnapshot, normalizeData) => {
  const data = docSnapshot?.data ? docSnapshot.data() : docSnapshot || {};

  return {
    id: docSnapshot?.id || data.id,
    ...normalizeData(data),
    isDeleted: data?.isDeleted === true,
    deletedAt: data?.deletedAt ?? null,
    createdAt: data?.createdAt ?? null,
    updatedAt: data?.updatedAt ?? null,
  };
};

const normalizeMixedProductInput = (docSnapshot) =>
  mapDocumentSnapshot(docSnapshot, (data) => ({
    name: normalizeString(data?.name),
    description: normalizeString(data?.description),
    unit: normalizeString(data?.unit),
    notes: normalizeString(data?.notes),
  }));

const normalizeMixedProductComponent = (docSnapshot) =>
  mapDocumentSnapshot(docSnapshot, (data) => ({
    productId: normalizeString(data?.productId),
    inputId: normalizeString(data?.inputId),
    name: normalizeString(data?.name),
    description: normalizeString(data?.description),
    quantity: normalizeNumber(data?.quantity, 1),
    unit: normalizeString(data?.unit),
    sortOrder: normalizeNumber(data?.sortOrder, 0),
    isRequired: normalizeBoolean(data?.isRequired, true),
    notes: normalizeString(data?.notes),
  }));

const normalizeBundle = (docSnapshot) =>
  mapDocumentSnapshot(docSnapshot, (data) => ({
    productId: normalizeString(data?.productId),
    name: normalizeString(data?.name),
    description: normalizeString(data?.description),
    status: normalizeStatus(data?.status),
    notes: normalizeString(data?.notes),
    isMain: normalizeBoolean(data?.isMain, false),
    createdBy: normalizeString(data?.createdBy),
    savedAt: data?.savedAt ?? null,
    snapshotVersion: normalizeNumber(data?.snapshotVersion, null),
    configurationSnapshot: normalizeSnapshotData(data?.configurationSnapshot),
    dimensions: normalizeConfigurationDimensions(
      data?.dimensions || data?.configurationSnapshot?.dimensions
    ),
    dimensionKey: normalizeString(data?.dimensionKey) ||
      getConfigurationDimensionKey(
        data?.dimensions || data?.configurationSnapshot?.dimensions
      ),
    duplicatedFromId: normalizeString(data?.duplicatedFromId),
  }));

const normalizeBundleComponent = (docSnapshot) =>
  mapDocumentSnapshot(docSnapshot, (data) => ({
    bundleId: normalizeString(data?.bundleId),
    sourceType: normalizeString(data?.sourceType) || 'mixedProductComponent',
    sourceId: normalizeString(data?.sourceId),
    name: normalizeString(data?.name),
    quantity: normalizeNumber(data?.quantity, 1),
    unit: normalizeString(data?.unit),
    sortOrder: normalizeNumber(data?.sortOrder, 0),
    isOptional: normalizeBoolean(data?.isOptional, false),
    notes: normalizeString(data?.notes),
  }));

const normalizeBundleExtraCost = (docSnapshot) =>
  mapDocumentSnapshot(docSnapshot, (data) => ({
    bundleId: normalizeString(data?.bundleId),
    name: normalizeString(data?.name),
    costCategory: normalizeString(data?.costCategory),
    amount: normalizeNumber(data?.amount, null),
    unit: normalizeString(data?.unit),
    sortOrder: normalizeNumber(data?.sortOrder, 0),
    notes: normalizeString(data?.notes),
  }));

const buildTimestampPayload = (payload, { isCreate = false } = {}) => ({
  ...payload,
  ...(isCreate
    ? {
      isDeleted: false,
      deletedAt: null,
      createdAt: serverTimestamp(),
    }
    : {}),
  updatedAt: serverTimestamp(),
});

const normalizeAuditActor = (currentUser) => {
  if (!currentUser?.uid) {
    return null;
  }

  const displayName = normalizeString(
    currentUser.displayName || currentUser.email
  );

  return {
    uid: currentUser.uid,
    name: displayName,
    displayName,
    email: normalizeString(currentUser.email).toLowerCase(),
    role: '',
    source: 'user',
  };
};

const withAuditActor = (payload, fieldName = 'updatedBy') => {
  const actor = normalizeAuditActor(auth?.currentUser);

  return actor
    ? {
      ...payload,
      [fieldName]: actor,
    }
    : payload;
};

const buildMixedProductInputPayload = (data = {}) => ({
  name: normalizeString(data?.name),
  description: normalizeString(data?.description),
  unit: normalizeString(data?.unit),
  notes: normalizeString(data?.notes),
});

const buildMixedProductComponentPayload = (data = {}) => ({
  productId: normalizeString(data?.productId),
  inputId: normalizeString(data?.inputId),
  name: normalizeString(data?.name),
  description: normalizeString(data?.description),
  quantity: normalizeNumber(data?.quantity, 1),
  unit: normalizeString(data?.unit),
  sortOrder: normalizeNumber(data?.sortOrder, 0),
  isRequired: normalizeBoolean(data?.isRequired, true),
  notes: normalizeString(data?.notes),
});

const hasOwn = (data, key) =>
  Object.prototype.hasOwnProperty.call(data || {}, key);

const buildBundlePayload = (data = {}) => {
  const payload = {
    name: normalizeString(data?.name),
    description: normalizeString(data?.description),
    status: normalizeStatus(data?.status),
    notes: normalizeString(data?.notes),
  };

  if (hasOwn(data, 'productId')) {
    payload.productId = normalizeString(data?.productId);
  }

  if (hasOwn(data, 'savedAt')) {
    payload.savedAt = data?.savedAt ?? null;
  }

  if (hasOwn(data, 'snapshotVersion')) {
    payload.snapshotVersion = normalizeNumber(data?.snapshotVersion, null);
  }

  if (hasOwn(data, 'configurationSnapshot')) {
    payload.configurationSnapshot = normalizeSnapshotData(
      data?.configurationSnapshot
    );
  }

  if (hasOwn(data, 'dimensions') || hasOwn(data, 'configurationSnapshot')) {
    const dimensions = normalizeConfigurationDimensions(
      data?.dimensions || data?.configurationSnapshot?.dimensions
    );

    payload.dimensions = dimensions;
    payload.dimensionKey = getConfigurationDimensionKey(dimensions);
  }

  if (hasOwn(data, 'duplicatedFromId')) {
    payload.duplicatedFromId = normalizeString(data?.duplicatedFromId);
  }

  if (hasOwn(data, 'isMain')) {
    payload.isMain = normalizeBoolean(data?.isMain, false);
  }

  if (hasOwn(data, 'createdBy')) {
    payload.createdBy = normalizeString(data?.createdBy);
  }

  return payload;
};

const buildBundleComponentPayload = (data = {}) => ({
  bundleId: normalizeString(data?.bundleId),
  sourceType: normalizeString(data?.sourceType) || 'mixedProductComponent',
  sourceId: normalizeString(data?.sourceId),
  name: normalizeString(data?.name),
  quantity: normalizeNumber(data?.quantity, 1),
  unit: normalizeString(data?.unit),
  sortOrder: normalizeNumber(data?.sortOrder, 0),
  isOptional: normalizeBoolean(data?.isOptional, false),
  notes: normalizeString(data?.notes),
});

const buildBundleExtraCostPayload = (data = {}) => ({
  bundleId: normalizeString(data?.bundleId),
  name: normalizeString(data?.name),
  costCategory: normalizeString(data?.costCategory),
  amount: normalizeNumber(data?.amount, null),
  unit: normalizeString(data?.unit),
  sortOrder: normalizeNumber(data?.sortOrder, 0),
  notes: normalizeString(data?.notes),
});

const createCollectionService = ({
  collectionName,
  normalizeDocument,
  buildPayload,
}) => {
  const getAll = async ({ includeDeleted = false } = {}) => {
    const querySnapshot = await getDocs(collection(db, collectionName));
    const records = querySnapshot.docs.map((docSnapshot) =>
      normalizeDocument(docSnapshot)
    );

    return records
      .filter((record) => includeDeleted || record.isDeleted !== true)
      .sort((firstRecord, secondRecord) =>
        (firstRecord.name || '').localeCompare(secondRecord.name || '')
      );
  };

  const getById = async (id) => {
    const normalizedId = normalizeString(id);

    if (!normalizedId) {
      return null;
    }

    const docSnapshot = await getDoc(doc(db, collectionName, normalizedId));

    return docSnapshot.exists() ? normalizeDocument(docSnapshot) : null;
  };

  const create = async (data = {}) => {
    const payload = withAuditActor(
      buildTimestampPayload(buildPayload(data), { isCreate: true }),
      'createdByAudit'
    );
    const docRef = await addDoc(collection(db, collectionName), payload);

    return normalizeDocument({
      id: docRef.id,
      ...payload,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
  };

  const update = async (id, data = {}) => {
    const normalizedId = normalizeString(id);

    if (!normalizedId) {
      return null;
    }

    const payload = withAuditActor(buildTimestampPayload(buildPayload(data)));

    await updateDoc(doc(db, collectionName, normalizedId), payload);

    return getById(normalizedId);
  };

  const softDelete = async (id) => {
    const normalizedId = normalizeString(id);

    if (!normalizedId) {
      return null;
    }

    await updateDoc(doc(db, collectionName, normalizedId), withAuditActor({
      isDeleted: true,
      deletedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }));

    return { success: true, id: normalizedId };
  };

  const restore = async (id) => {
    const normalizedId = normalizeString(id);

    if (!normalizedId) {
      return null;
    }

    await updateDoc(doc(db, collectionName, normalizedId), withAuditActor({
      isDeleted: false,
      deletedAt: null,
      updatedAt: serverTimestamp(),
    }));

    return getById(normalizedId);
  };

  const remove = async (id) => {
    const normalizedId = normalizeString(id);

    if (!normalizedId) {
      return { success: false };
    }

    await deleteDoc(doc(db, collectionName, normalizedId));

    return { success: true, id: normalizedId };
  };

  return {
    getAll,
    getById,
    create,
    update,
    softDelete,
    restore,
    remove,
  };
};

const mixedProductInputsService = createCollectionService({
  collectionName: BUNDLE_ARCHITECTURE_COLLECTIONS.mixedProductInputs,
  normalizeDocument: normalizeMixedProductInput,
  buildPayload: buildMixedProductInputPayload,
});

const mixedProductComponentsService = createCollectionService({
  collectionName: BUNDLE_ARCHITECTURE_COLLECTIONS.mixedProductComponents,
  normalizeDocument: normalizeMixedProductComponent,
  buildPayload: buildMixedProductComponentPayload,
});

const bundlesService = createCollectionService({
  collectionName: BUNDLE_ARCHITECTURE_COLLECTIONS.bundles,
  normalizeDocument: normalizeBundle,
  buildPayload: buildBundlePayload,
});

const bundleComponentsService = createCollectionService({
  collectionName: BUNDLE_ARCHITECTURE_COLLECTIONS.bundleComponents,
  normalizeDocument: normalizeBundleComponent,
  buildPayload: buildBundleComponentPayload,
});

const bundleExtraCostsService = createCollectionService({
  collectionName: BUNDLE_ARCHITECTURE_COLLECTIONS.bundleExtraCosts,
  normalizeDocument: normalizeBundleExtraCost,
  buildPayload: buildBundleExtraCostPayload,
});

export const getMixedProductInputs = mixedProductInputsService.getAll;
export const getMixedProductInputById = mixedProductInputsService.getById;
export const createMixedProductInput = mixedProductInputsService.create;
export const updateMixedProductInput = mixedProductInputsService.update;
export const softDeleteMixedProductInput = mixedProductInputsService.softDelete;
export const restoreMixedProductInput = mixedProductInputsService.restore;
export const deleteMixedProductInput = mixedProductInputsService.remove;

export const getMixedProductComponents = mixedProductComponentsService.getAll;
export const getMixedProductComponentById = mixedProductComponentsService.getById;
export const createMixedProductComponent = mixedProductComponentsService.create;
export const updateMixedProductComponent = mixedProductComponentsService.update;
export const softDeleteMixedProductComponent = mixedProductComponentsService.softDelete;
export const restoreMixedProductComponent = mixedProductComponentsService.restore;
export const deleteMixedProductComponent = mixedProductComponentsService.remove;

export const getBundles = bundlesService.getAll;
export const getBundleById = bundlesService.getById;
export const createBundle = bundlesService.create;
export const updateBundle = bundlesService.update;
export const softDeleteBundle = bundlesService.softDelete;
export const restoreBundle = bundlesService.restore;
export const deleteBundle = bundlesService.remove;

export const getBundleComponents = bundleComponentsService.getAll;
export const getBundleComponentById = bundleComponentsService.getById;
export const createBundleComponent = bundleComponentsService.create;
export const updateBundleComponent = bundleComponentsService.update;
export const softDeleteBundleComponent = bundleComponentsService.softDelete;
export const restoreBundleComponent = bundleComponentsService.restore;
export const deleteBundleComponent = bundleComponentsService.remove;

export const getBundleExtraCosts = bundleExtraCostsService.getAll;
export const getBundleExtraCostById = bundleExtraCostsService.getById;
export const createBundleExtraCost = bundleExtraCostsService.create;
export const updateBundleExtraCost = bundleExtraCostsService.update;
export const softDeleteBundleExtraCost = bundleExtraCostsService.softDelete;
export const restoreBundleExtraCost = bundleExtraCostsService.restore;
export const deleteBundleExtraCost = bundleExtraCostsService.remove;
